import { pulseItemName, pulseItemCtaLabel } from '@/modules/pulse/utils/pulseItemDisplay';
import { PULSE_ACTION_TYPE } from '@/modules/pulse/utils/pulseConstants';

// Legacy/other-producer per-channel prepared-message fields, tried only when the
// primary `suggested_reply` is absent. Real channel selection (via the Contact
// Available Channels API) is the handling phase, so no channel is shown here.
const CHANNEL_MESSAGE_FIELDS = [
  { field: 'sms_message', label: 'SMS' },
  { field: 'email_message', label: 'Email' },
  { field: 'FB_message', label: 'Facebook' },
  { field: 'other_message', label: 'Message' },
];

// actions[].payload may arrive as an object or a JSON string.
const parsePayload = (payload) => {
  if (payload && typeof payload === 'object') return payload;
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }
  return null;
};

const trimmed = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

// Builds the closed-artifact preview (message body + optional channel label) from
// a reply action's prepared content. The client_agent producer sends a single
// `suggested_reply` (no channel); older/other shapes may send per-channel fields.
// Returns null when no prepared message is present.
const buildReplyArtifact = (payload) => {
  const suggested = trimmed(payload.suggested_reply);
  if (suggested) {
    const artifact = { content: suggested };
    const channel = payload.channel_label || payload.channel;
    if (channel) artifact.channel_label = channel;
    return artifact;
  }
  const match = CHANNEL_MESSAGE_FIELDS.find(({ field }) => trimmed(payload[field]));
  if (!match) return null;
  return { channel_label: match.label, content: trimmed(payload[match.field]) };
};

const buildChatWithBizAiArtifact = (payload) => {
  const content = trimmed(payload.action_context_text);
  return content ? { content } : null;
};

// Per-action box body; absent from here means no box. See the README's card-shape table.
const ARTIFACT_BUILDER = {
  [PULSE_ACTION_TYPE.REPLY]: buildReplyArtifact,
  [PULSE_ACTION_TYPE.CHAT_WITH_BIZAI]: buildChatWithBizAiArtifact,
};

// Reasoning leads with `actions[0].reason`, then this per-action fallback (see README).
const REASONING_FALLBACK = {
  [PULSE_ACTION_TYPE.CHAT_WITH_BIZAI]: (payload) => payload.action_context_title,
};

const defaultReasoningFallback = (payload, item) => item?.display?.title;

// What an item's `context_uid` actually names, per context type — and the only two the
// client card can resolve. Any other context (appointment, payment, document, estimate…)
// names an entity `app.client` cannot open: passed as a matter uid it would be resolved
// through `getMatter` and fail, so such an item carries no nav ids at all and its View
// client chip is hidden instead (see the recommendation README, "Events").
const CONTEXT_UID_FIELD = {
  matter: 'matter_uid',
  client: 'client_uid',
};

const contextUidAs = (item, field) => {
  const { context_type: type, context_uid: uid } = item?.context || {};
  return CONTEXT_UID_FIELD[type] === field ? uid || null : null;
};

// The uid the Conversita card is keyed on. See the recommendation README, "`cardUid` is not
// `actionUid`".
export const recommendationCardUid = (item) => item?.actions?.[0]?.uid || item?.uid || null;

// Maps a Pulse Item to the recommendation card rendered in Conversita, plus the
// topic title shown above it. Everything is derived from the item payload —
// opening a Pulse Item renders the card locally, with no call to aiagents.
//
// The topic title is "<topicPrefix> - <context/matter name>"; the caller owns the prefix
// (and its i18n). See the README's "Topic title".
const buildRecommendationLaunch = (item, topicPrefix = '') => {
  const action = item?.actions?.[0] || {};
  const payload = parsePayload(action.payload) || {};
  const buildArtifact = ARTIFACT_BUILDER[action.action];
  const artifact = buildArtifact ? buildArtifact(payload) : null;
  // Primary CTA label: the backend's button text (e.g. "Reply"), then the payload. Shared
  // with the tile so both read the same (see the README's "CTA label").
  const ctaLabel = pulseItemCtaLabel(item) || payload.cta_label;
  if (artifact && ctaLabel) artifact.cta_label = ctaLabel;
  const reasoningFallback = REASONING_FALLBACK[action.action] || defaultReasoningFallback;
  const reasoning = action.reason || reasoningFallback(payload, item) || '';
  const title = [topicPrefix, pulseItemName(item)].filter(Boolean).join(' - ');
  const ctaPayload = payload.cta_payload || {};
  return {
    title,
    recommendation: {
      type: 'recommendation',
      action: action.action || null,
      uid: recommendationCardUid(item),
      recommendation_uid: item?.recommendation_uid || item?.uid || null,
      client_uid: payload.client_uid || ctaPayload.client_uid || contextUidAs(item, 'client_uid'),
      matter_uid: payload.matter_uid || contextUidAs(item, 'matter_uid'),
      reasoning,
      artifact,
    },
  };
};

export default buildRecommendationLaunch;
